import React, { forwardRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { t } from 'i18next';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import CircularProgress from '@mui/material/CircularProgress';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';

interface MailProps {
  mailHost: string;
  setMailHost: (v: string) => void;
  mailPort: string;
  setMailPort: (v: string) => void;
  mailSll: boolean;
  setMailSll: (v: any) => void;
  mailUser: string;
  setMailUser: (v: string) => void;
  showPassword: boolean;
  mailPassword: string;
  setMailPassword: (v: string) => void;
  setShowPassword: (v: boolean) => void;
  mailAuth: string;
  setMailAuth: (v: string) => void;
  mailFrom: string;
  setMailFrom: (v: string) => void;
  mailName: string;
  setMailName: (v: string) => void;
  mailService: string;
  setMailService: (v: string) => void;
  handleSaveMail: () => void;
  handleCheckMail: () => void;
  isLoading: boolean;
  isLoadingMail: boolean;
}

const labelCls = 'text-[11px] font-semibold text-[var(--text-color-calendar-week)] mb-[3px] block';
const inputCls = 'w-full text-[13px] py-[5px] px-[8px] rounded-[5px] bg-[var(--bg-input)] text-[var(--text-color)] border border-[var(--border-input)] outline-none';

const Field: React.FC<{ label: string; children: React.ReactNode; full?: boolean }> = ({ label, children, full }) => (
  <div className={full ? 'col-span-2' : ''} style={{ display: 'flex', flexDirection: 'column' }}>
    <label className={labelCls}>{label}</label>
    {children}
  </div>
);

const Mail: React.FC<MailProps> = ({
  mailHost, setMailHost,
  mailPort, setMailPort,
  mailSll, setMailSll,
  mailUser, setMailUser,
  showPassword, mailPassword, setMailPassword, setShowPassword,
  mailAuth, setMailAuth,
  mailFrom, setMailFrom,
  mailName, setMailName,
  mailService, setMailService,
  handleSaveMail, handleCheckMail,
  isLoading, isLoadingMail,
}) => {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 16px', padding: '4px 0' }}>

      {/* Host */}
      <Field label="Mail Host">
        <input className={inputCls} type="text" title="Mail Host" placeholder="smtp.example.com" value={mailHost} onChange={e => setMailHost(e.target.value)} />
      </Field>

      {/* Port */}
      <Field label="Mail Port">
        <input className={inputCls} type="text" title="Mail Port" placeholder="587" value={mailPort} onChange={e => setMailPort(e.target.value.replace(/\D/g, ''))} />
      </Field>

      {/* User */}
      <Field label="Mail User">
        <input className={inputCls} type="text" title="Mail User" placeholder="user@example.com" value={mailUser} onChange={e => setMailUser(e.target.value)} />
      </Field>

      {/* Password */}
      <Field label="Mail Password">
        <div style={{ position: 'relative' }}>
          <input
            type={showPassword ? 'text' : 'password'}
            value={mailPassword}
            className={inputCls}
            onChange={e => setMailPassword(e.target.value)}
          />
          <FontAwesomeIcon
            icon={showPassword ? faEyeSlash : faEye}
            style={{ position: 'absolute', top: '50%', transform: 'translateY(-50%)', right: 8, fontSize: 12, opacity: 0.5, cursor: 'pointer' }}
            className="text-[var(--text-color)] hover:opacity-70"
            onClick={() => { if (mailPassword !== '*****') setShowPassword(!showPassword); }}
          />
        </div>
      </Field>

      {/* From email */}
      <Field label="From Email">
        <input className={inputCls} type="text" value={mailFrom} onChange={e => setMailFrom(e.target.value)} placeholder="noreply@company.com" />
      </Field>

      {/* Display name */}
      <Field label="Display Name">
        <input className={inputCls} type="text" value={mailName} onChange={e => setMailName(e.target.value)} placeholder="Company Name" />
      </Field>

      {/* Service */}
      <Field label="Mail Service">
        <select className={inputCls} value={mailService} onChange={e => setMailService(e.target.value)}>
          <option value="" disabled>Select service</option>
          <option value="gmail">Gmail</option>
          <option value="other">Other</option>
        </select>
      </Field>

      {/* Auth */}
      <Field label="Auth Method">
        <select className={inputCls} value={mailAuth} onChange={e => setMailAuth(e.target.value)}>
          <option value="" disabled>Select auth type</option>
          <option value="PLAIN">PLAIN</option>
          <option value="LOGIN">LOGIN</option>
        </select>
      </Field>

      {/* TLS checkbox */}
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <FormControlLabel
          control={<Checkbox onChange={() => setMailSll((prev: any) => !prev)} checked={mailSll} size="small" />}
          label="TLS / SSL"
          sx={{ '& .MuiFormControlLabel-label': { fontSize: '13px', color: 'var(--text-color)' } }}
        />
      </div>

      {/* Hint */}
      <div style={{ display: 'flex', alignItems: 'center', fontSize: 11, color: 'var(--text-color-calendar-week)' }}>
        Display Name показывается получателям в поле «От»
      </div>

      {/* Buttons */}
      <div className="col-span-2" style={{ display: 'flex', gap: 10, marginTop: 4 }}>
        <button
          type="button"
          onClick={handleCheckMail}
          disabled={isLoadingMail}
          className="flex-1 bg-[var(--btn-user-btn)] text-[var(--text-color)] border border-[var(--border-input)] py-[6px] px-[12px] rounded-[5px] text-[12px] cursor-pointer hover:bg-[var(--btn-user-hover)] disabled:opacity-50"
        >
          {isLoadingMail ? <CircularProgress size={14} color="inherit" /> : t('Test Mail')}
        </button>
        <button
          type="button"
          onClick={handleSaveMail}
          disabled={isLoading}
          className="flex-1 bg-[var(--btn-user-btn)] text-[var(--text-color)] border border-[var(--border-input)] py-[6px] px-[12px] rounded-[5px] text-[12px] cursor-pointer hover:bg-[var(--btn-user-hover)] disabled:opacity-50"
          style={{ borderColor: 'rgba(5,150,105,.4)' }}
        >
          {isLoading ? <CircularProgress size={14} color="inherit" /> : t('Save Change')}
        </button>
      </div>
    </div>
  );
};

export default Mail;
